/** Routing, and the gate between signed out and signed in.
 *
 * **There are two apps in here**, and the split is the point.
 *
 * `Routed` below is the admin: it is behind `useAuth`, and an unauthenticated
 * visitor gets `<LoginPage/>` before its router is ever reached. That was the
 * whole of this file until T-38, and it is why seven anonymous API surfaces —
 * registration, abstracts, evaluation, the gala, the survey, Q&A and a
 * delegate's own identity — existed with nothing anywhere that rendered them.
 *
 * `PUBLIC` is the other one: a small set of paths that must work with no
 * session at all, matched *before* the gate. They are branded from the client
 * rather than from this admin, they mount no providers the admin needs, and
 * they call `publicApi` rather than `api`, which is what keeps a page with no
 * credential from reaching for one.
 *
 * The prefixes are one letter for a reason a person will meet: these URLs are
 * pasted into emails and read aloud down a telephone. `/f/<token>` is a form
 * and `/n/...` is news.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { AcceptInvitePage } from "@/auth/AcceptInvite";
import { AuthProvider, useAuth } from "@/auth/AuthProvider";
import { LoginPage } from "@/auth/LoginPage";
import { ResetPasswordPage } from "@/auth/PasswordReset";
import { FirstEventForm } from "@/components/FirstEventForm";
import { ErrorState, PageLoading, Panel, Skeleton } from "@/components/ui/states";
import { api } from "@/lib/api";
import { newestEvent } from "@/lib/events";
import { lazyPage } from "@/lib/lazyPage";

const AttendPage = lazyPage(() => import("@/public/BlockPages"), "AttendPage");
const TakePartPage = lazyPage(() => import("@/public/TakePartPage"), "TakePartPage");
const CertificatePage = lazyPage(() => import("@/public/CertificatePage"), "CertificatePage");
const BlockAskPage = lazyPage(() => import("@/public/BlockPages"), "BlockAskPage");
const BlockWallPage = lazyPage(() => import("@/public/BlockPages"), "BlockWallPage");
const AbstractCallPage = lazyPage(() => import("@/public/AbstractPages"), "AbstractCallPage");
const ReviewPage = lazyPage(() => import("@/public/AbstractPages"), "ReviewPage");
const EvaluatePage = lazyPage(() => import("@/public/FeedbackPages"), "EvaluatePage");
const SurveyPage = lazyPage(() => import("@/public/FeedbackPages"), "SurveyPage");
const GalaPage = lazyPage(() => import("@/public/GalaPage"), "GalaPage");
const NotifyConfirmPage = lazyPage(() => import("@/public/NotifyPages"), "NotifyConfirmPage");
const NotifyStopPage = lazyPage(() => import("@/public/NotifyPages"), "NotifyStopPage");
const ProfilePage = lazyPage(() => import("@/public/ProfilePage"), "ProfilePage");
const QaAskPage = lazyPage(() => import("@/public/QaPages"), "QaAskPage");
const QaFeedPage = lazyPage(() => import("@/public/QaPages"), "QaFeedPage");
const RegistrationPage = lazyPage(
  () => import("@/public/RegistrationPage"),
  "RegistrationPage",
);
const PayDonePage = lazyPage(() => import("@/public/PayDonePage"), "PayDonePage");
const ActivityPage = lazyPage(() => import("@/routes/ActivityPage"), "ActivityPage");
const AssetsPage = lazyPage(() => import("@/routes/AssetsPage"), "AssetsPage");
const CollectionPage = lazyPage(() => import("@/routes/CollectionPage"), "CollectionPage");
const ContactsPage = lazyPage(() => import("@/routes/ContactsPage"), "ContactsPage");
const EnquiriesPage = lazyPage(() => import("@/routes/EnquiriesPage"), "EnquiriesPage");
const EntryPage = lazyPage(() => import("@/routes/EntryPage"), "EntryPage");
const EventHome = lazyPage(() => import("@/routes/EventHome"), "EventHome");
const EventOperations = lazyPage(() => import("@/routes/EventHome"), "EventOperations");
const EventWebsite = lazyPage(() => import("@/routes/EventHome"), "EventWebsite");
const EventPage = lazyPage(() => import("@/routes/EventPage"), "EventPage");
const FormEditorPage = lazyPage(() => import("@/routes/FormEditorPage"), "FormEditorPage");
const FormsPage = lazyPage(() => import("@/routes/FormsPage"), "FormsPage");
const PeoplePage = lazyPage(() => import("@/routes/PeoplePage"), "PeoplePage");
const ReleasesPage = lazyPage(() => import("@/routes/ReleasesPage"), "ReleasesPage");
const ScanPage = lazyPage(() => import("@/routes/ScanPage"), "ScanPage");
const SettingsPage = lazyPage(() => import("@/routes/SettingsPage"), "SettingsPage");
const Shell = lazyPage(() => import("@/routes/Shell"), "Shell");
const MembersPage = lazyPage(() => import("@/routes/MembersPage"), "MembersPage");
const SingletonPage = lazyPage(() => import("@/routes/SingletonPage"), "SingletonPage");
const SitesPage = lazyPage(() => import("@/routes/SitesPage"), "SitesPage");
const StoragePage = lazyPage(() => import("@/routes/StoragePage"), "StoragePage");
const SystemPage = lazyPage(() => import("@/routes/SystemPage"), "SystemPage");
const JobsPage = lazyPage(() => import("@/routes/JobsPage"), "JobsPage");
const TimePage = lazyPage(() => import("@/routes/TimePage"), "TimePage");
const ResourcesPage = lazyPage(() => import("@/routes/ResourcesPage"), "ResourcesPage");
const LogsPage = lazyPage(() => import("@/routes/LogsPage"), "LogsPage");
const PaymentsLogPage = lazyPage(() => import("@/routes/PaymentsLogPage"), "PaymentsLogPage");
const LearnRustPage = lazyPage(() => import("@/routes/LearnRustPage"), "LearnRustPage");
const TasksPage = lazyPage(() => import("@/routes/TasksPage"), "TasksPage");
const BadgeKiosk = lazyPage(() => import("@/routes/BadgeKiosk"), "BadgeKiosk");
const HallScreen = lazyPage(() => import("@/routes/HallScreen"), "HallScreen");

/** Sends someone landing on a bare site key straight into its newest event.
 *
 * It used to land on the site's first collection. After D10 there is no such
 * thing: content belongs to an event, so `welcome` is not a place you can go
 * until you have said whose welcome.
 *
 * It then landed on the events list, which for a conference with one event is a
 * page asking you to pick the thing you have just picked. So it resolves the
 * event here instead and goes to the workspace — the page with the actual work
 * on it. A conference is one event now (D-44), so there is no list to fall back
 * to: a conference without one says so here.
 *
 * The query key matches `EventHome`'s, so the workspace it lands on reads this
 * fetch from the cache rather than asking again. */
export function SiteIndex() {
  const { site = "" } = useParams();
  const events = useQuery({ queryKey: ["events", site], queryFn: () => api.listEvents(site) });

  if (events.isPending) {
    return (
      <Panel>
        <Skeleton rows={6} label="Opening this conference" />
      </Panel>
    );
  }

  if (events.isError) {
    return (
      <Panel>
        <ErrorState message={events.error.message} onRetry={() => void events.refetch()} />
      </Panel>
    );
  }

  const opening = newestEvent(events.data);
  if (!opening) {
    return <NoEventYet site={site} />;
  }
  return <Navigate to={`/${site}/events/${opening.id}`} replace />;
}

/** A conference with no event says so, and offers to make it (BACKLOG 111):
 * a site declared in code is synced without one, and nothing else here could
 * add it. */
function NoEventYet({ site }: { site: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return (
    <Panel>
      <div className="mx-auto grid max-w-lg gap-4 px-6 py-10">
        <p className="text-center text-md text-ink-muted">This conference has no event.</p>
        <FirstEventForm
          site={site}
          onCreated={async (made) => {
            await queryClient.invalidateQueries({ queryKey: ["events", site] });
            navigate(`/${site}/events/${made.id}`, { replace: true });
          }}
        />
      </div>
    </Panel>
  );
}

/** An old `/:site/people` or `/:site/enquiries` link, sent
 * to the same page without the conference in the path. The link's query
 * survives; the conference moves into `?site=`, which People reads as the
 * conference its actions (a new person, a portrait) are filed under. */
export function ToCentral() {
  const { site = "" } = useParams();
  const { pathname, search, hash } = useLocation();
  const rest = pathname.slice(`/${site}`.length);
  const query = new URLSearchParams(search);
  query.set("site", site);
  return <Navigate to={{ pathname: rest, search: `?${query}`, hash }} replace />;
}

export function Routed() {
  const { user, restoring } = useAuth();

  if (restoring) {
    // The cookie exchange is fast, but not instant. Flashing the login form
    // and then replacing it is worse than a moment of nothing.
    return (
      <div role="status" aria-busy="true" className="grid min-h-dvh place-items-center">
        <span className="text-md text-ink-muted">Signing you in…</span>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const first = user.sites[0];
  // An administrator is never stuck here: the conferences list is where they
  // make the first one.
  if (!first && !user.is_admin) {
    return (
      <main className="grid min-h-dvh place-items-center px-4">
        <div className="max-w-sm text-center">
          <h1 className="text-xl font-semibold text-ink">No sites yet</h1>
          <p className="mt-1 text-md text-ink-muted">
            Your account does not have access to any site. Ask an administrator to grant you a
            role.
          </p>
        </div>
      </main>
    );
  }

  return (
    <Routes>
      {/* One site is not a choice, so it is not worth a screen asking for one —
          unless you are the one who can make a second. */}
      {first && user.sites.length === 1 && !user.is_admin ? (
        <Route path="/" element={<Navigate to={`/${first.key}`} replace />} />
      ) : (
        <>
          <Route path="/" element={<Shell />}>
            <Route index element={<SitesPage />} />
          </Route>
          {/* A literal outranks `/:site`, so "past" is a reserved key. */}
          <Route path="/past" element={<Shell />}>
            <Route index element={<SitesPage past />} />
          </Route>
        </>
      )}
      {/* The installation's rather than a conference's: CPU, memory and the
          queue are facts about the machine every client shares. A literal
          outranks `/:site`, so a conference keyed "system" could not be
          opened here; none is. */}
      {user.is_admin && (
        <Route path="/system" element={<Shell />}>
          <Route index element={<SystemPage />} />
          {/* Pages of their own, off a System page grown too full (D-80). */}
          <Route path="jobs" element={<JobsPage />} />
          <Route path="time" element={<TimePage />} />
          <Route path="resources" element={<ResourcesPage />} />
        </Route>
      )}
      {/* The badge kiosk at the entrance (D-87): the whole screen, no sidebar.
          A literal outranks `/:site`, so no conference may be keyed "kiosk" (nor "learn" below). */}
      <Route path="/kiosk/:site/:eventId" element={<BadgeKiosk />} />
      {/* A vote's, questionnaire's or draw's hall screen (D-183), as full as the kiosk. */}
      <Route path="/screen/:site/:eventId/:id" element={<HallScreen />} />
      {/* The tutorial lives beside the installation's pages: an
          administrator's, and the code it quotes is private. */}
      {user.is_admin && (
        <Route path="/learn" element={<Shell />}>
          <Route path="rust" element={<LearnRustPage />} />
          <Route path="rust/:chapter" element={<LearnRustPage />} />
        </Route>
      )}
      {/* Everything anybody changed, every conference at once (D-66). */}
      {user.is_admin && (
        <Route path="/logs" element={<Shell />}>
          <Route index element={<LogsPage />} />
          <Route path="payments" element={<PaymentsLogPage />} />
        </Route>
      )}
      {/* The office team's own board and shared folders, outside every
          conference (D-55). Assigned to me became the board's Mine filter;
          its old address lands there with Mine on. */}
      {user.is_admin && (
        <Route path="/team" element={<Shell />}>
          <Route path="tasks" element={<TasksPage />} />
          <Route path="storage" element={<StoragePage />} />
          <Route path="storage/:folderId" element={<StoragePage />} />
          <Route path="members" element={<MembersPage />} />
          <Route path="assigned" element={<Navigate to="/team/tasks?mine=1" replace />} />
        </Route>
      )}
      {/* The directory and the enquiries belong to no conference (D9, D-19), so
          neither is under one. A literal outranks `/:site`: no conference may
          be keyed "people" or "enquiries". The forms people fill in joined
          them (2026-10-25): a form is a conference's only when it is for one
          of its events, and a general one is nobody's but Future Minds'. `new`
          is a literal, so it outranks `:formId`. */}
      <Route path="/people" element={<Shell />}>
        <Route index element={<PeoplePage />} />
        <Route path="forms" element={<FormsPage />} />
        <Route path="forms/new" element={<FormEditorPage />} />
        <Route path="forms/:formId" element={<FormEditorPage />} />
      </Route>
      {/* An enquiry arrives before there is a conference to file it under. */}
      <Route path="/enquiries" element={<Shell />}>
        <Route index element={<EnquiriesPage />} />
      </Route>
      <Route path="/:site" element={<Shell />}>
        <Route index element={<SiteIndex />} />
        {/* The events list went with D-44; an old link lands on the conference. */}
        <Route path="events" element={<Navigate to=".." relative="path" replace />} />
        <Route path="events/:eventId" element={<EventHome />} />
        {/* The event's two halves (D-98). Literals, so they outrank `:tab`
            below whatever order they are written in. */}
        <Route path="events/:eventId/website" element={<EventWebsite />} />
        <Route path="events/:eventId/operations" element={<EventOperations />} />
        {/* Content and publishing sit inside the event, because after D10
            they cannot be addressed without one. `releases` and `:tab` are
            both one segment past the event id; the router ranks a literal
            above a parameter, so Publishing wins that match regardless of the
            order they are written in here. */}
        <Route path="events/:eventId/c/:collection" element={<CollectionPage />} />
        <Route path="events/:eventId/c/:collection/:entryId" element={<EntryPage />} />
        <Route path="events/:eventId/s/:singleton" element={<SingletonPage />} />
        <Route path="events/:eventId/releases" element={<ReleasesPage />} />
        <Route path="events/:eventId/:tab" element={<EventPage />} />
        <Route path="scan" element={<ScanPage />} />
        <Route path="assets" element={<AssetsPage />} />
        {/* People, Enquiries and the forms left the conference's address; an
            old link lands on the central page, remembering which conference
            it came from. */}
        <Route path="people" element={<ToCentral />} />
        <Route path="people/forms" element={<ToCentral />} />
        <Route path="people/forms/new" element={<ToCentral />} />
        <Route path="people/forms/:formId" element={<ToCentral />} />
        <Route path="enquiries" element={<ToCentral />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="activity" element={<ActivityPage />} />
        <Route path="settings" element={<SettingsPage />} />
        {/* Where it used to be, for a bookmark. */}
        <Route
          path="system"
          element={<Navigate to={user.is_admin ? "/system" : ".."} relative="path" replace />}
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Every path that is answered without a session.
 *
 * Declared as data rather than as JSX inside `App`, so "which pages are open"
 * has a one-line answer — the same discipline `cms/main.py` applies on the
 * server, where the anonymous routers live in their own modules so that
 * `test_ops_access.py` can require 401 of everything else.
 */
const PUBLIC = [
  { path: "/f/:token", element: <ProfilePage /> },
  // Registration and the other forms a stranger is sent, hosted here rather
  // than on a client site (D-29). The server builds each link to one of these.
  { path: "/r/:token", element: <RegistrationPage /> },
  // Where a payer lands from a payment link, or a door that named no page (plan 3).
  { path: "/pay/done", element: <PayDonePage /> },
  { path: "/abstracts/:token", element: <AbstractCallPage /> },
  { path: "/abstracts/review/:token", element: <ReviewPage /> },
  { path: "/gala/:token", element: <GalaPage /> },
  { path: "/survey/:token", element: <SurveyPage /> },
  { path: "/evaluate/:token", element: <EvaluatePage /> },
  { path: "/qa/:token", element: <QaAskPage /> },
  { path: "/qa/feed/:token", element: <QaFeedPage /> },
  // A block's two QRs and its wall (D-70).
  { path: "/attend/:token", element: <AttendPage /> },
  { path: "/take-part/:token", element: <TakePartPage /> },
  // Every event's certificate download page, for its website to link to (D-185).
  { path: "/certificate/:site/:slug", element: <CertificatePage /> },
  { path: "/qa/b/:token", element: <BlockAskPage /> },
  { path: "/qa/b/feed/:token", element: <BlockWallPage /> },
  { path: "/n/confirm/:token", element: <NotifyConfirmPage /> },
  { path: "/n/stop/:token", element: <NotifyStopPage /> },
  // A staff member with no session, holding the link a reset mailed them. The
  // token is after the hash, where no server sees it (auth/PasswordReset.tsx).
  { path: "/reset-password", element: <ResetPasswordPage /> },
  // An invited administrator, before they have a password (D-62); the token
  // rides after the hash the same way.
  { path: "/accept-invite", element: <AcceptInvitePage /> },
];

export function App() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Before the provider, not inside it. `AuthProvider` attempts a refresh
          on mount; a visitor holding a form link has no cookie to refresh, and
          putting these inside would make every public page wait on a request
          that is going to fail — and briefly render "Signing you in…" to
          somebody who is not signing in. */}
        {PUBLIC.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
        <Route
          path="*"
          element={
            <AuthProvider>
              <Routed />
            </AuthProvider>
          }
        />
      </Routes>
    </Suspense>
  );
}
